import { useCallback, useEffect, useRef } from 'react';
import { useWebSocketContext, type WebSocketMessage } from '@zg-rentals/web-socket-react';
import { isSatelliteWebSocketEvent } from '@zg-rentals/satellite-web-socket-helpers';

type Options = {
  conversationId: string | undefined;
  refetchMessages: () => void;
};

/**
 * Real-time updates for the open conversation's message list, via the shared satellite-websocket
 * module. Owns the refetch for the messages it renders rather than being signalled by a list hook
 * through an Apollo reactive variable (the prior coupling removed in RCRM-6660).
 *
 * Refetches when a `newMessage` targets the displayed conversation — including events on the
 * conversation-scoped topic that omit the id (the message list knows which conversation it shows).
 * Also refetches once after a transient disconnect (`errorRecoveryCount` increments) to catch
 * messages missed during the gap. WS-off polling lives in `useConversationMessagesQuery`, so this
 * hook doesn't poll. Mirrors the SRW MessageList pattern.
 */
export default function useMessageListRealtimeUpdates({ conversationId, refetchMessages }: Options) {
  const { errorRecoveryCount, isWebSocketEnabledAndReady, subscribeToMessages } = useWebSocketContext();

  // Read the conversation id and refetch through refs so the message handler identity stays stable
  // when the user switches conversations — otherwise every change would tear down and re-establish
  // the WebSocket subscription.
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const refetchMessagesRef = useRef(refetchMessages);
  refetchMessagesRef.current = refetchMessages;

  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    if (!isSatelliteWebSocketEvent(message.event)) return;
    if (!conversationIdRef.current) return;
    // Only `newMessage` adds to the open thread; the list view owns the list-level events.
    if (message.event.type !== 'newMessage') return;

    // A missing conversationId means the event came on our conversation-scoped topic, so it's ours.
    const incomingConversationId = message.event.conversationId;
    if (!incomingConversationId || incomingConversationId === conversationIdRef.current) {
      refetchMessagesRef.current();
    }
  }, []);

  useEffect(() => {
    if (!isWebSocketEnabledAndReady) return;
    return subscribeToMessages(handleWebSocketMessage);
  }, [isWebSocketEnabledAndReady, subscribeToMessages, handleWebSocketMessage]);

  // Refetch after a transient WebSocket disconnect — the query-level poll fallback is gated on
  // `!isWebSocketEnabledAndReady`, so messages that arrived during the gap would otherwise be lost.
  const prevErrorRecoveryCountRef = useRef(errorRecoveryCount);
  useEffect(() => {
    if (!isWebSocketEnabledAndReady) return;
    if (errorRecoveryCount !== prevErrorRecoveryCountRef.current) {
      prevErrorRecoveryCountRef.current = errorRecoveryCount;
      if (conversationIdRef.current) {
        refetchMessagesRef.current();
      }
    }
  }, [isWebSocketEnabledAndReady, errorRecoveryCount]);
}
